import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import {
  ArrowRight,
  MapPin,
  Search,
  ClipboardCheck,
  Home as HomeIcon,
  DollarSign,
  ShieldCheck,
  UserCheck,
  Globe2,
  Heart,
  Sparkles,
  Phone,
  X,
  ZoomIn,
} from "lucide-react";
import Reveal from "@/components/motion/Reveal";
import HexMark from "@/components/motion/HexMark";

const VENUES = [
  { src: "/venues/01-executive-presentation.webp", label: "Executive Presentation Room" },
  { src: "/venues/02-general-session.jpg", label: "General Session · Convention" },
  { src: "/venues/03-gala-awards.jpg", label: "Gala · Awards Dinner" },
  { src: "/venues/04-roundtable-strategy.jpg", label: "Roundtable · Strategy Room" },
  { src: "/venues/05-reception-banquet.jpg", label: "Reception · Banquet Hall" },
  { src: "/venues/06-open-air-performance.jpg", label: "Live Performance · Open-Air" },
];

const ROOMS = [
  { src: "/rooms/01-executive-suite.webp", label: "Executive Suite" },
  { src: "/rooms/02-boutique-resort-grounds.jpg", label: "Boutique Resort Grounds" },
  { src: "/rooms/03-living-room-suite.jpg", label: "Living Room Suite" },
];

const WEDDING = "/weddings/hero-ceremony-drapes.jpg";

const WEDDING_GALLERY = [
  {
    src: "/weddings/01-beachfront-ceremony.jpg",
    label: "Beachfront Ceremony",
    href: null,
  },
  {
    src: "/weddings/02-reception-lounge.jpeg",
    label: "Reception Lounge",
    href: null,
  },
  {
    src: "/weddings/03-dion-magee-events-ad.png",
    label: "Dion Magee Events & Weddings",
    href: "https://dionmageeevents.com",
  },
];

const HERO_IMG = "/home/hero-2.png";

const PILLARS = [
  { icon: ClipboardCheck, k: "Time recovered", body: "We absorb 100% of the venue-search burden — you focus on the vision, we handle the logistics." },
  { icon: ShieldCheck, k: "Risk kept in check", body: "No cost to you, and every contract read carefully to protect your budget and legal exposure." },
  { icon: Sparkles, k: "Curated excellence", body: "Every recommendation is hand-picked to elevate the guest experience — not just fill a room." },
];

const SERVICES = [
  { icon: Search, name: "Location & Venue Search", body: "Hand-selected venues and hotel blocks aligned with your vision, budget, and audience." },
  { icon: ClipboardCheck, name: "Supplier Search & Sourcing", body: "Vetted vendor networks — e.g. catering, AV, decor, transportation — sourced and negotiated on your behalf." },
  { icon: Heart, name: "Wedding Destination & Hotel Rooms Search", body: "International wedding packages, room blocks, and destination perks — negotiated for you." },
  { icon: ClipboardCheck, name: "Full Reporting & Approval", body: "Transparent side-by-side comparisons so you can decide fast, with full context." },
  { icon: HomeIcon, name: "Employee Relocation & Housing", body: "Seamless relocation support so your team is settled and cared for." },
  { icon: DollarSign, name: "Cost-Saving Options", body: "Deep industry partnerships translate to real budget leverage across logistics and decor." },
  { icon: ShieldCheck, name: "Risk & Liability Reduction", body: "Contract negotiation and risk review that puts your interests first." },
  { icon: UserCheck, name: "Personalized Account Managers", body: "A dedicated consultant who knows your preferences and manages every detail." },
  { icon: Globe2, name: "Global On-Site Staffing", body: "Qualified onsite directors and project managers to execute your vision anywhere." },
];

export default function SiteSelection() {
  const [lightbox, setLightbox] = useState(null);
  const openLightbox = useCallback((src, alt) => setLightbox({ src, alt }), []);
  const closeLightbox = useCallback(() => setLightbox(null), []);

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => { if (e.key === "Escape") closeLightbox(); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [lightbox, closeLightbox]);

  useEffect(() => {
    const prev = document.title;
    document.title = "Site Selection & Sourcing · Magee Enterprises";
    return () => { document.title = prev; };
  }, []);

  return (
    <div className="bg-white text-[var(--text)]">
      <Seo
        title="Site Selection & Sourcing — Venues, Hotels, Destinations"
        description="Hand-picked venues, hotel blocks, and destinations — sourced with strategy and negotiated with muscle. Wedding, corporate, employee relocation, and global on-site staffing, at no cost to you."
        path="/services/site-selection"
        image={HERO_IMG}
        keywords="venue sourcing, hotel sourcing, site selection, destination wedding planning, hotel block negotiation, corporate venue search, DMC, employee relocation"
      />
      {/* HERO */}
      <section className="relative pt-28 md:pt-32 px-4 md:px-6">
        <div className="relative max-w-[1600px] mx-auto rounded-[28px] md:rounded-[36px] overflow-hidden min-h-[560px] md:min-h-[640px]">
          <img src={HERO_IMG} alt="Curated beachfront resort venue" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050A14]/50 via-[#050A14]/40 to-[#050A14]/85" />
          <div className="absolute -top-32 -left-24 w-[500px] h-[500px] rounded-full bg-cyan-400/20 blur-3xl" />
          <div className="absolute -bottom-32 -right-24 w-[600px] h-[600px] rounded-full bg-sky-500/20 blur-3xl" />

          <div className="relative px-4 md:px-14 pt-16 md:pt-24 pb-24 md:pb-28">
            <div className="flex items-center justify-center">
              <span className="chip !bg-white/10 !text-cyan-300 !border-cyan-300/25">
                <MapPin size={12} /> Site Selection &amp; Sourcing
              </span>
            </div>
            <h1 className="hero-headline text-white text-center mt-8 md:mt-10 leading-[0.95] text-[46px] sm:text-[60px] md:text-[76px] lg:text-[92px] max-w-5xl mx-auto text-balance px-2">
              The perfect venue —{" "}
              <span className="hero-accent">found for you.</span>
            </h1>
            <div className="flex justify-center mt-8"><span className="pulse-bar w-32 md:w-48" /></div>
            <p className="text-center text-white/85 text-base md:text-lg leading-relaxed max-w-3xl mx-auto mt-8">
              A venue is more than square footage — it is the foundation of every
              moment your guests will remember. We source with strategy, negotiate
              with muscle, and hand you a shortlist worth choosing from.
            </p>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
              <Link to="/contact" className="btn-primary">Request a Quote <ArrowRight size={16} /></Link>
              <a href="tel:+18882992250" className="btn-dark backdrop-blur">
                <Phone size={14} /> 888-299-2250
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* 3 PILLARS */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 hex-pattern opacity-40 pointer-events-none" />
        <div className="brand-bloom brand-bloom-cyan w-[500px] h-[500px] -top-32 -left-40 opacity-35" />
        <HexMark size={220} opacity={0.055} className="absolute -right-10 top-24 rotate-[14deg] pointer-events-none hidden lg:block" />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
          <Reveal>
            <span className="chip"><span className="chip-dot" /> Why partner with us</span>
            <h2 className="mt-6 font-editorial text-4xl md:text-6xl leading-[1] tracking-[-0.03em] max-w-3xl">
              Stress-free support —{" "}
              <span className="bg-gradient-to-r from-cyan-500 to-sky-600 bg-clip-text text-transparent italic font-medium">at no cost to you.</span>
            </h2>
            <p className="mt-6 text-[var(--text-muted)] max-w-2xl">
              We take on the top three pain points — time, money, and satisfaction —
              so you can focus on your event, not the search.
            </p>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            {PILLARS.map((p, i) => (
              <Reveal key={p.k} delay={i * 0.08}>
                <div className="grad-border card-white p-6 md:p-8 h-full relative">
                  <span className="corner-cross" style={{ top: 10, right: 10 }} />
                  <div className="icon-tile mb-6 relative z-10"><p.icon size={20} /></div>
                  <div className="numeral-mono text-4xl md:text-5xl absolute top-3 right-4 select-none">0{i + 1}</div>
                  <h3 className="font-editorial text-xl leading-tight">{p.k}</h3>
                  <p className="mt-3 text-[var(--text-muted)] text-sm leading-relaxed">{p.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES GRID */}
      <section className="relative py-20 md:py-28 bg-[var(--surface)] overflow-hidden">
        <div className="absolute inset-0 hex-pattern opacity-40 pointer-events-none" />
        <div className="relative max-w-[1600px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16">
              <div>
                <span className="chip"><span className="chip-dot" /> Services</span>
                <h2 className="mt-6 font-editorial text-4xl md:text-6xl leading-[1] tracking-[-0.03em] max-w-3xl">
                  Nine ways we make your stress{" "}
                  <span className="bg-gradient-to-r from-cyan-500 to-sky-600 bg-clip-text text-transparent italic font-medium">disappear.</span>
                </h2>
              </div>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {SERVICES.map((s, i) => (
              <Reveal key={s.name} delay={i * 0.04}>
                <div className="card-white p-6 h-full">
                  <div className="icon-tile mb-5"><s.icon size={18} /></div>
                  <span className="font-mono-brand text-[10px] uppercase tracking-[0.22em] text-[var(--text-muted)]">
                    /{String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="font-editorial text-lg leading-tight mt-1">{s.name}</h3>
                  <p className="mt-3 text-[var(--text-muted)] text-[13px] leading-relaxed">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* VENUE SPACES GALLERY */}
      <section className="relative py-16 md:py-24">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10">
          <Reveal>
            <span className="chip"><span className="chip-dot" /> Venue Spaces</span>
            <h2 className="mt-6 font-editorial text-3xl md:text-5xl leading-[1] tracking-[-0.03em] max-w-3xl">
              A curated network across{" "}
              <span className="bg-gradient-to-r from-cyan-500 to-sky-600 bg-clip-text text-transparent italic font-medium">every setting.</span>
            </h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-4">
            {VENUES.map((v, i) => (
              <Reveal key={v.src} delay={i * 0.04}>
                <button
                  type="button"
                  onClick={() => openLightbox(v.src, v.label)}
                  data-testid={`gallery-venue-${i + 1}`}
                  aria-label={`Enlarge ${v.label}`}
                  className="relative aspect-[4/3] md:aspect-[4/3] overflow-hidden rounded-2xl group w-full block cursor-zoom-in focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 text-left"
                >
                  <img src={v.src} alt={v.label} className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-[1200ms]" />
                  <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-[#050A14]/80 via-[#050A14]/25 to-transparent" />
                  <span className="absolute top-3 left-3 chip !bg-white/85 !text-[var(--text)] !border-white/50 !py-1 !px-2 !text-[9px]">
                    /{String(i + 1).padStart(2, "0")}
                  </span>
                  <div className="absolute bottom-4 left-4 right-4 text-white">
                    <p className="font-mono-brand text-[10px] uppercase tracking-[0.24em] text-cyan-300/85 mb-1">
                      Venue Space
                    </p>
                    <p className="font-editorial text-lg md:text-xl leading-tight tracking-[-0.01em]">
                      {v.label}
                    </p>
                  </div>
                  <span aria-hidden className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <span className="w-12 h-12 rounded-full bg-white/15 backdrop-blur-md border border-white/45 text-white flex items-center justify-center shadow-[0_10px_30px_-8px_rgba(0,0,0,0.5)] ring-1 ring-inset ring-white/10">
                      <ZoomIn size={20} strokeWidth={2.2} />
                    </span>
                  </span>
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SLEEPING ROOMS */}
      <section className="relative py-16 md:py-24 bg-[var(--surface)]">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10">
          <Reveal>
            <span className="chip"><span className="chip-dot" /> Sleeping Rooms</span>
            <h2 className="mt-6 font-editorial text-3xl md:text-5xl leading-[1] tracking-[-0.03em] max-w-3xl">
              Where your guests actually{" "}
              <span className="bg-gradient-to-r from-cyan-500 to-sky-600 bg-clip-text text-transparent italic font-medium">rest well.</span>
            </h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-4">
            {ROOMS.map((r, i) => (
              <Reveal key={r.src} delay={i * 0.04}>
                <button
                  type="button"
                  onClick={() => openLightbox(r.src, r.label)}
                  data-testid={`gallery-room-${i + 1}`}
                  aria-label={`Enlarge ${r.label}`}
                  className="relative aspect-[4/3] overflow-hidden rounded-2xl group w-full block cursor-zoom-in focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 text-left"
                >
                  <img src={r.src} alt={r.label} className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-[1200ms]" />
                  <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-[#050A14]/80 via-[#050A14]/25 to-transparent" />
                  <span className="absolute top-3 left-3 chip !bg-white/85 !text-[var(--text)] !border-white/50 !py-1 !px-2 !text-[9px]">
                    /{String(i + 1).padStart(2, "0")}
                  </span>
                  <div className="absolute bottom-4 left-4 right-4 text-white">
                    <p className="font-mono-brand text-[10px] uppercase tracking-[0.24em] text-cyan-300/85 mb-1">
                      Sleeping Room
                    </p>
                    <p className="font-editorial text-lg md:text-xl leading-tight tracking-[-0.01em]">
                      {r.label}
                    </p>
                  </div>
                  <span aria-hidden className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <span className="w-12 h-12 rounded-full bg-white/15 backdrop-blur-md border border-white/45 text-white flex items-center justify-center shadow-[0_10px_30px_-8px_rgba(0,0,0,0.5)] ring-1 ring-inset ring-white/10">
                      <ZoomIn size={20} strokeWidth={2.2} />
                    </span>
                  </span>
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* MID-PAGE BOOK-A-CALL BANNER */}
      <section className="relative py-12 md:py-16">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="relative rounded-3xl overflow-hidden card-dark px-6 md:px-12 py-10 md:py-12 group">
              <div className="absolute inset-0 grid-pattern-dark opacity-40 pointer-events-none" />
              <div className="absolute -top-16 -right-20 w-[360px] h-[360px] rounded-full bg-cyan-500/25 blur-3xl" />

              <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                <div className="flex items-start gap-4">
                  <span className="shrink-0 w-11 h-11 rounded-full bg-cyan-500/15 border border-cyan-300/30 flex items-center justify-center text-cyan-300">
                    <Sparkles size={18} />
                  </span>
                  <div>
                    <p className="font-mono-brand text-[10px] uppercase tracking-[0.24em] text-cyan-300/85">
                      Not ready to submit a full RFP?
                    </p>
                    <h3 className="mt-2 font-editorial text-2xl md:text-3xl leading-tight text-white text-balance max-w-2xl">
                      Grab 15 minutes with our site-selection team.
                    </h3>
                    <p className="mt-2 text-white/60 text-[14.5px] leading-relaxed max-w-xl">
                      We&rsquo;ll scout out 3 short-listed venues that match your dates and vibe — no obligation, no follow-up spam.
                    </p>
                  </div>
                </div>
                <Link
                  to="/contact#book"
                  data-testid="site-selection-mid-book-cta"
                  className="btn-primary shrink-0 w-full md:w-auto justify-center group-hover:translate-x-0.5 transition-transform"
                >
                  Book a 15-min call <ArrowRight size={14} />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* WEDDINGS */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="brand-bloom brand-bloom-warm w-[460px] h-[460px] -top-24 -right-24 opacity-35" />
        <div className="relative max-w-[1600px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-6 md:gap-10">
          <Reveal className="col-span-12 lg:col-span-7">
            <button
              type="button"
              onClick={() => openLightbox(WEDDING, "Weddings · Anywhere. Anytime.")}
              data-testid="gallery-weddings-hero"
              aria-label="Enlarge weddings hero image"
              className="group relative aspect-[4/3] overflow-hidden rounded-2xl w-full block cursor-zoom-in focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 text-left"
            >
              <img src={WEDDING} alt="Wedding table setting" className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-[1200ms]" />
              <div className="absolute top-5 left-5">
                <span className="chip !bg-white/85 !text-[var(--text)] !border-white/50">
                  <Heart size={12} /> Weddings · Anywhere. Anytime.
                </span>
              </div>
              <span aria-hidden className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/25">
                <span className="w-11 h-11 rounded-full bg-white/95 text-[var(--text)] flex items-center justify-center shadow-lg">
                  <ZoomIn size={18} />
                </span>
              </span>
            </button>
          </Reveal>
          <div className="col-span-12 lg:col-span-5 flex flex-col justify-center">
            <Reveal delay={0.1}>
              <h2 className="font-editorial text-4xl md:text-5xl lg:text-6xl leading-[1] tracking-[-0.03em]">
                Your vision.{" "}
                <span className="bg-gradient-to-r from-cyan-500 to-sky-600 bg-clip-text text-transparent italic font-medium">Our global map.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 text-[var(--text-muted)] text-lg leading-relaxed">
                A wedding should be defined by the destination, the details, and the
                joy of the moment — never the stress of the search. We hand-pick
                venues and hotel blocks that match your style and budget, absorbing
                the complexities of global sourcing so you remain the visionary and
                we serve as the architects of the dream.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <Link to="/contact" className="btn-primary mt-8 w-fit">
                Submit an inquiry <ArrowRight size={14} />
              </Link>
            </Reveal>
          </div>
        </div>

        {/* Wedding Gallery */}
        <div className="relative max-w-[1600px] mx-auto px-6 md:px-10 mt-14 md:mt-20">
          {/* Row 1: two standard tiles */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-4">
            {WEDDING_GALLERY.filter((w) => !w.href).map((w, i) => (
              <Reveal key={w.src} delay={i * 0.04}>
                <button
                  type="button"
                  onClick={() => openLightbox(w.src, w.label)}
                  data-testid={`gallery-wedding-${i + 1}`}
                  aria-label={`Enlarge ${w.label}`}
                  className="relative aspect-[4/3] overflow-hidden rounded-2xl group w-full block cursor-zoom-in focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 text-left"
                >
                  <img
                    src={w.src}
                    alt={w.label}
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-[1200ms]"
                  />
                  <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-[#050A14]/80 via-[#050A14]/25 to-transparent" />
                  <span className="absolute top-3 left-3 chip !bg-white/85 !text-[var(--text)] !border-white/50 !py-1 !px-2 !text-[9px]">
                    /{String(i + 1).padStart(2, "0")}
                  </span>
                  <div className="absolute bottom-4 left-4 right-4 text-white">
                    <p className="font-mono-brand text-[10px] uppercase tracking-[0.24em] text-cyan-300/85 mb-1">
                      Weddings
                    </p>
                    <p className="font-editorial text-lg md:text-xl leading-tight tracking-[-0.01em]">
                      {w.label}
                    </p>
                  </div>
                  <span aria-hidden className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <span className="w-12 h-12 rounded-full bg-white/15 backdrop-blur-md border border-white/45 text-white flex items-center justify-center shadow-[0_10px_30px_-8px_rgba(0,0,0,0.5)] ring-1 ring-inset ring-white/10">
                      <ZoomIn size={20} strokeWidth={2.2} />
                    </span>
                  </span>
                </button>
              </Reveal>
            ))}
          </div>

          {/* Row 2: sponsored partner spotlight (Dion Magee Events) */}
          {WEDDING_GALLERY.filter((w) => w.href).map((w) => (
            <Reveal key={w.src} delay={0.1}>
              <div className="relative mt-6 md:mt-8">
                {/* Kicker meta */}
                <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                  <div className="flex items-center gap-3">
                    <span className="font-mono-brand text-[10px] uppercase tracking-[0.28em] text-amber-500">
                      /03 · Sponsored Partner Spotlight
                    </span>
                    <span className="hidden md:inline-block h-px w-16 bg-gradient-to-r from-amber-400/60 to-transparent" />
                  </div>
                  <span className="chip !bg-amber-50 !text-amber-700 !border-amber-200 !text-[9px]">
                    <Sparkles size={11} /> Featured Ad
                  </span>
                </div>

                {/* Card with animated gold gradient border */}
                <a
                  href={w.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="wedding-partner-link"
                  className="relative block group rounded-3xl overflow-hidden"
                >
                  {/* Gold shine border */}
                  <span
                    aria-hidden="true"
                    className="pointer-events-none absolute -inset-[1px] rounded-3xl opacity-90 group-hover:opacity-100 transition-opacity duration-500"
                    style={{
                      background:
                        "conic-gradient(from 180deg at 50% 50%, rgba(214,167,72,0) 0deg, rgba(214,167,72,0.55) 90deg, rgba(255,225,150,0.9) 180deg, rgba(214,167,72,0.55) 270deg, rgba(214,167,72,0) 360deg)",
                    }}
                  />
                  <div className="relative rounded-3xl bg-[#0A0A0D] overflow-hidden">
                    {/* Ambient glow */}
                    <div className="pointer-events-none absolute -top-24 -left-24 w-[420px] h-[420px] rounded-full bg-amber-500/15 blur-3xl" />
                    <div className="pointer-events-none absolute -bottom-24 -right-24 w-[420px] h-[420px] rounded-full bg-rose-400/10 blur-3xl" />

                    <div className="relative flex flex-col lg:flex-row items-stretch gap-6 md:gap-10 p-6 md:p-10 lg:p-14">
                      {/* Left · Image with generous frame */}
                      <div className="relative lg:flex-[1.35] flex items-center justify-center min-h-[280px]">
                        <div className="relative w-full max-w-[720px] mx-auto">
                          <img
                            src={w.src}
                            alt={w.label}
                            className="w-full h-auto rounded-xl shadow-[0_25px_60px_-15px_rgba(214,167,72,0.35)] transform group-hover:scale-[1.02] transition-transform duration-[900ms] ease-out"
                          />
                          {/* subtle shine sweep */}
                          <span
                            aria-hidden="true"
                            className="pointer-events-none absolute inset-0 rounded-xl overflow-hidden"
                          >
                            <span className="absolute top-0 -left-1/3 h-full w-1/3 bg-gradient-to-r from-transparent via-white/25 to-transparent -skew-x-12 opacity-0 group-hover:opacity-100 group-hover:animate-[shimmer_1.4s_ease_forwards]" />
                          </span>
                        </div>
                      </div>

                      {/* Right · Copy */}
                      <div className="lg:flex-[0.9] flex flex-col justify-center text-white">
                        <span className="font-mono-brand text-[10px] uppercase tracking-[0.28em] text-amber-300/90 mb-3">
                          Weddings by Dion Magee
                        </span>
                        <h3 className="font-editorial text-3xl md:text-4xl lg:text-5xl leading-[1.02] tracking-[-0.02em] text-white">
                          Extraordinary celebrations.{" "}
                          <span className="italic font-medium bg-gradient-to-r from-amber-300 via-amber-200 to-amber-400 bg-clip-text text-transparent">
                            Flawlessly crafted.
                          </span>
                        </h3>
                        <p className="mt-5 text-white/70 text-base md:text-lg leading-relaxed max-w-md">
                          Full-service wedding planning and design for couples who
                          expect the exceptional — a sister agency of Magee Enterprises.
                        </p>

                        <div className="mt-6 flex flex-wrap items-center gap-3">
                          <span className="inline-flex items-center gap-2 rounded-full bg-white/5 border border-white/10 px-3 py-1.5 text-[11px] text-white/80 font-mono-brand uppercase tracking-[0.14em]">
                            Full-Service Planning
                          </span>
                          <span className="inline-flex items-center gap-2 rounded-full bg-white/5 border border-white/10 px-3 py-1.5 text-[11px] text-white/80 font-mono-brand uppercase tracking-[0.14em]">
                            Custom Design
                          </span>
                          <span className="inline-flex items-center gap-2 rounded-full bg-white/5 border border-white/10 px-3 py-1.5 text-[11px] text-white/80 font-mono-brand uppercase tracking-[0.14em]">
                            Stress-Free
                          </span>
                        </div>

                        <div className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-amber-400 to-amber-500 text-[#1A1204] font-semibold text-sm shadow-[0_15px_35px_-10px_rgba(214,167,72,0.55)] w-fit group-hover:shadow-[0_20px_45px_-10px_rgba(214,167,72,0.8)] group-hover:translate-y-[-2px] transition-all duration-300">
                          Visit dionmageeevents.com
                          <ArrowRight size={16} />
                        </div>

                        <p className="mt-4 font-mono-brand text-[10px] uppercase tracking-[0.24em] text-white/40">
                          www.dionmageeevents.com
                        </p>
                      </div>
                    </div>
                  </div>
                </a>
              </div>
            </Reveal>
          ))}

          {/* Row 3: additional wedding gallery */}
          <div className="mt-6 md:mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-4">
            {[
              { src: "/weddings/04-wisteria-garden-banquet.jpg", label: "Garden Banquet · Wisteria Canopy" },
              { src: "/weddings/05-conservatory-reception.jpg", label: "Conservatory Reception" },
              { src: "/weddings/06-themed-ceremony.jpg", label: "Ceremony · Themed Design" },
            ].map((w, i) => (
              <Reveal key={w.src} delay={i * 0.04}>
                <button
                  type="button"
                  onClick={() => openLightbox(w.src, w.label)}
                  data-testid={`gallery-wedding-${i + 4}`}
                  aria-label={`Enlarge ${w.label}`}
                  className="relative aspect-[4/3] overflow-hidden rounded-2xl group w-full block cursor-zoom-in focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 text-left"
                >
                  <img
                    src={w.src}
                    alt={w.label}
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-[1200ms]"
                  />
                  <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-[#050A14]/80 via-[#050A14]/25 to-transparent" />
                  <span className="absolute top-3 left-3 chip !bg-white/85 !text-[var(--text)] !border-white/50 !py-1 !px-2 !text-[9px]">
                    /{String(i + 4).padStart(2, "0")}
                  </span>
                  <div className="absolute bottom-4 left-4 right-4 text-white">
                    <p className="font-mono-brand text-[10px] uppercase tracking-[0.24em] text-cyan-300/85 mb-1">
                      Weddings
                    </p>
                    <p className="font-editorial text-lg md:text-xl leading-tight tracking-[-0.01em]">
                      {w.label}
                    </p>
                  </div>
                  <span aria-hidden className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <span className="w-12 h-12 rounded-full bg-white/15 backdrop-blur-md border border-white/45 text-white flex items-center justify-center shadow-[0_10px_30px_-8px_rgba(0,0,0,0.5)] ring-1 ring-inset ring-white/10">
                      <ZoomIn size={20} strokeWidth={2.2} />
                    </span>
                  </span>
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <div className="card-dark relative overflow-hidden px-8 md:px-16 py-20 md:py-24 text-center">
            <div className="absolute inset-0 grid-pattern-dark opacity-40" />
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[70%] h-[300px] cyan-radial" />
            <div className="relative">
              <span className="chip !bg-white/5 !text-cyan-300 !border-cyan-300/25">
                <Phone size={12} /> Ready to search — smarter?
              </span>
              <h2 className="mt-8 font-editorial text-4xl md:text-6xl lg:text-7xl leading-[1] tracking-[-0.03em] text-white max-w-4xl mx-auto text-balance">
                Reclaim your time.{" "}
                <span className="bg-gradient-to-r from-cyan-300 to-sky-400 bg-clip-text text-transparent italic font-medium">Keep the peace of mind.</span>
              </h2>
              <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
                <Link to="/contact" className="btn-primary">Request a Quote <ArrowRight size={14} /></Link>
                <a href="tel:+18882992250" className="btn-dark border-white/20">
                  <Phone size={14} /> 888-299-2250
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* LIGHTBOX — click any gallery photo to enlarge */}
      {lightbox && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={`Enlarged image: ${lightbox.alt}`}
          onClick={closeLightbox}
          data-testid="site-selection-lightbox"
          className="fixed inset-0 z-[200] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4 md:p-8 animate-in fade-in duration-300"
        >
          <button
            type="button"
            onClick={closeLightbox}
            aria-label="Close enlarged image"
            data-testid="site-selection-lightbox-close"
            className="absolute top-4 right-4 md:top-6 md:right-6 z-10 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 border border-white/25 text-white flex items-center justify-center transition-colors backdrop-blur"
          >
            <X size={22} />
          </button>
          <div
            className="relative max-w-[92vw] max-h-[88vh]"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={lightbox.src}
              alt={lightbox.alt}
              className="max-w-[92vw] max-h-[88vh] object-contain rounded-lg shadow-[0_30px_100px_-20px_rgba(0,0,0,0.7)]"
            />
            {lightbox.alt && (
              <p className="mt-3 text-center text-white/70 text-sm font-mono-brand uppercase tracking-[0.2em]">
                {lightbox.alt}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
